export type Disposer = () => void;
export interface ReadonlySignal<T> { readonly value: T }
export interface Signal<T> extends ReadonlySignal<T> { value: T; readonly version: number }
type Source = { subscribers: Set<Observer> };
type Observer = { dependencies: Set<Source>; notify: () => void };
let active: Observer | undefined;
let owner: Scope | undefined;
let depth = 0;
let flushing = false;
const queue = new Set<() => void>();

export class Scope {
  private cleanups = new Set<Disposer>();
  private children = new Set<Scope>();
  private parent = owner;
  disposed = false;
  constructor() { this.parent?.children.add(this); }
  run<T>(fn: () => T): T {
    if (this.disposed) throw new Error('Cannot run a disposed scope');
    const previous = owner;
    owner = this;
    try { return fn(); } finally { owner = previous; }
  }
  add(fn: Disposer): void { if (this.disposed) fn(); else this.cleanups.add(fn); }
  dispose = (): void => {
    if (this.disposed) return;
    this.disposed = true;
    const previous = active; active = undefined;
    const errors: unknown[] = [];
    for (const dispose of [...this.children].map(child => child.dispose).concat([...this.cleanups].reverse())) {
      try { dispose(); } catch (error) { errors.push(error); }
    }
    this.children.clear(); this.cleanups.clear(); this.parent?.children.delete(this);
    active = previous;
    if (errors.length) throw new AggregateError(errors, 'Scope cleanup failed');
  };
}
export function createScope(): Scope { return new Scope(); }
export function getScope(): Scope | undefined { return owner; }
export function onCleanup(fn: Disposer): void {
  if (!owner) throw new Error('onCleanup requires an active scope');
  owner.add(fn);
}
export function onMount(fn: () => void | Disposer): void {
  const scope = owner;
  if (!scope) throw new Error('onMount requires an active scope');
  queueMicrotask(() => { if (!scope.disposed) scope.run(() => { const dispose = fn(); if (dispose !== undefined) { if (typeof dispose !== 'function') throw new TypeError('onMount cleanup must be a function'); onCleanup(dispose); } }); });
}
export function untrack<T>(fn: () => T): T {
  const previous = active; active = undefined;
  try { return fn(); } finally { active = previous; }
}
function track(source: Source): void {
  if (active) { source.subscribers.add(active); active.dependencies.add(source); }
}
function detach(observer: Observer): void {
  for (const source of observer.dependencies) source.subscribers.delete(observer);
  observer.dependencies.clear();
}
function flush(): void {
  if (depth || flushing) return;
  flushing = true;
  const visits = new Map<() => void, number>();
  try {
    while (queue.size) {
      const job = queue.values().next().value!; queue.delete(job);
      const count = (visits.get(job) ?? 0) + 1; visits.set(job, count);
      if (count > 1000) { queue.clear(); throw new Error('Reactive cycle: an effect updated more than 1000 times in one flush'); }
      job();
    }
  } catch (error) { queue.clear(); throw error; } finally { flushing = false; }
}
function publish(source: Source): void {
  depth++;
  try { for (const observer of [...source.subscribers]) observer.notify(); }
  finally { depth--; flush(); }
}
export function signal<T>(initial: T): Signal<T> {
  let value = initial, version = 0;
  const source: Source = { subscribers: new Set() };
  return {
    get value() { track(source); return value; },
    set value(next) { if (!Object.is(value, next)) { value = next; version++; publish(source); } },
    get version() { return version; }
  };
}
export function computed<T>(fn: () => T): ReadonlySignal<T> {
  let dirty = true, running = false, value: T;
  const source: Source = { subscribers: new Set() };
  const observer: Observer = { dependencies: new Set(), notify() {
    if (!dirty) { dirty = true; publish(source); }
  } };
  owner?.add(() => detach(observer));
  return { get value() {
    track(source);
    if (dirty) {
      if (running) throw new Error('Computed cycle detected');
      detach(observer);
      const previous = active; active = observer; running = true;
      try { value = fn(); dirty = false; } finally { active = previous; running = false; }
    }
    return value;
  } };
}
export function effect(fn: () => void | Disposer): Disposer {
  const scope = new Scope();
  let iteration: Scope | undefined, stopped = false;
  const run = () => {
    if (stopped || scope.disposed) return;
    detach(observer); iteration?.dispose();
    const previous = active; active = observer;
    try { scope.run(() => {
      iteration = new Scope();
      iteration.run(() => { const cleanup = fn(); if (cleanup !== undefined) { if (typeof cleanup !== 'function') throw new TypeError('Effects must be synchronous; cleanup must be a function'); onCleanup(cleanup); } });
    }); } finally { active = previous; }
  };
  const observer: Observer = { dependencies: new Set(), notify() { if (!scope.disposed) queue.add(run); } };
  scope.add(() => { stopped = true; queue.delete(run); detach(observer); });
  try { batch(run); } catch (error) { scope.dispose(); throw error; }
  return scope.dispose;
}
/** Effects run synchronously after the outermost batch, before returning to the caller. */
export function batch<T>(fn: () => T): T {
  depth++;
  try { return fn(); } finally { depth--; flush(); }
}
